import { getUrlParam } from './params';

const STORAGE_KEY = 'mlabs_mcp_oauth_params';
const MAX_AGE_MS = 10 * 60 * 1000;
let clearedRequestId;

const endMcpAttempt = (authRequestId) => {
  clearedRequestId = authRequestId;
  try {
    if (authRequestId) {
      // Keep only the closed attempt ID so reloading its URL cannot reopen it.
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ authRequestId, closed: true }));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
};

export const getStoredMcpOAuthParams = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const age = Date.now() - parsed?.createdAt;
    if (parsed?.closed || !parsed?.authRequestId || !Number.isFinite(age) || age < 0 || age >= MAX_AGE_MS) {
      endMcpAttempt(parsed?.authRequestId);
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
};

export const clearMcpOAuthParams = () => {
  let authRequestId = getUrlParam('mcp_auth_request_id');
  try {
    authRequestId = authRequestId || JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}')?.authRequestId;
  } catch {
    /* ignore invalid storage */
  }
  endMcpAttempt(authRequestId);
};

/**
 * Params sent by mlabs_full McpOauthController when the user is not logged in:
 *   /accounts/sign_in?mcp_auth_request_id=...&mcp_callback_url=...
 *
 * After login we must resume the bridge at:
 *   {FULL_URL}/mcp/oauth/authorize?auth_request_id=...
 * so the cookie authApiToken is read and the MCP OAuth callback completes.
 */
export const persistMcpOAuthParams = () => {
  const authRequestId = getUrlParam('mcp_auth_request_id');
  const callbackUrl = getUrlParam('mcp_callback_url');

  if (!authRequestId) {
    // Only actual login continuations may resume stored state. Sign-in also has
    // root and fallback routes, so checking just /accounts/sign_in is insufficient.
    const continuationPaths = ['/accounts/verify', '/oauth/callback', '/accounts/magic_link_verify'];
    if (!continuationPaths.includes((window.location.pathname || '').replace(/\/$/, ''))) {
      clearMcpOAuthParams();
      return {};
    }
    return getStoredMcpOAuthParams();
  }
  if (authRequestId === clearedRequestId) return {};

  const previous = getStoredMcpOAuthParams();
  if (authRequestId === clearedRequestId) return {};
  if (previous.authRequestId === authRequestId) return previous;

  const params = {
    authRequestId,
    ...(callbackUrl ? { callbackUrl } : {}),
    createdAt: Date.now(),
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    /* ignore quota / private mode */
  }

  return params;
};

export const getMcpAuthRequestId = () => {
  return persistMcpOAuthParams().authRequestId;
};

/**
 * Callback URL sent to auth_api and used after login.
 * When connecting MCP (ChatGPT etc.), resume the mlabs_full OAuth bridge
 * instead of landing on the main app.
 */
export const getLoginCallbackUrl = (defaultUrl) => {
  const fallback = defaultUrl || process.env.REACT_APP_FULL_URL;
  const authRequestId = getMcpAuthRequestId();
  if (!authRequestId) {
    return fallback;
  }

  const base = (process.env.REACT_APP_FULL_URL || fallback || '').replace(/\/$/, '');
  if (!base) {
    return fallback;
  }

  return `${base}/mcp/oauth/authorize?auth_request_id=${encodeURIComponent(authRequestId)}`;
};
